import React, {useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {
    Box,
    Button,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    TextField,
    Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {Form, Formik, FormikProps} from 'formik';
import MarketplaceDocumentViewer from '../components/marketplace/MarketplaceDocumentViewer';
import {useService} from '../../../infrastructure/service/service';
import {State} from '../../../entity/model/state';
import {County} from '../../../entity/model/county';
import {DropdownOption} from '../../../entity/core/dropdown-option';
import {MarketplaceSearchDto} from '../../../entity/dto/marketplace-search-dto';
import {User} from '../../../entity/model/user';
import {FilterMetadataRequestDto} from '../../../entity/dto/filter-metadata-request-dto';
import {MarketplaceRecordRequestDto} from '../../../entity/dto/marketplace-record-request-dto';
import {MetadataType} from "../../../entity/enums/metadata-type";
import {MarketplaceType} from "../../../entity/enums/marketplace-type";
import {BookType} from "../../../entity/enums/book-type";
import {ActionType} from "../../../entity/enums/action-type";
import {UserPermission} from "../../../entity/enums/user-permission";
import {toast} from "react-toastify";
import Grid from "@mui/material/Grid2";
import AddFundDialog from "../components/marketplace/AddFundDialog";
import PurchaseDialog from "../components/marketplace/PurchaseDialog";
import LoadingComponent from "../components/widget/LoadingComponent";

import '../../../assets/css/style.css';
import '../../../assets/css/pages/marketplace.css';
import '../../../assets/css/authentication/nav-bar.css';

const Marketplace = (): JSX.Element => {
    const {stateService, countyService, userService, marketplaceService, subscriptionService, documentService} = useService();

    const [currentUser, setCurrentUser] = useState<User>();
    const [selectedState, setSelectedState] = useState<string>('');
    const [selectedCounty, setSelectedCounty] = useState<string>('');
    const [selectedBookType, setSelectedBookType] = useState<string>('');
    const [selectedIndexType, setSelectedIndexType] = useState<string>('');
    const [dateRange, setDateRange] = useState<string>('');
    const [selectedIndexBook, setSelectedIndexBook] = useState<string>('');
    const [selectedRecordedBook, setSelectedRecordedBook] = useState<string>('');
    const [pageFrom, setPageFrom] = useState<string>('');
    const [pageTo, setPageTo] = useState<string>('');
    const [openPurchaseDialog, setOpenPurchaseDialog] = useState<boolean>(false);
    const [openAddFundsDialog, setOpenAddFundsDialog] = useState<boolean>(false);
    const [selectedDocumentUrl, setSelectedDocumentUrl] = useState<string>('');
    const [selectedDocumentName, setSelectedDocumentName] = useState<string>('');
    const [selectedDocumentMetadataID, setSelectedDocumentMetadataID] = useState<string>('');
    const [tokenBalance, setTokenBalance] = useState<number>(0);

    const [counties, setCounties] = useState<County[]>([]);
    const [stateOptions, setStateOptions] = useState<DropdownOption[]>([]);
    const [countyOptions, setCountyOptions] = useState<DropdownOption[]>([]);
    const [bookTypes, setBookTypes] = useState<string[]>([]);
    const [indexTypes, setIndexTypes] = useState<string[]>([]);
    const [dateRanges, setDateRanges] = useState<string[]>([]);
    const [indexBooks, setIndexBooks] = useState<string[]>([]);
    const [recordedBooks, setRecordedBooks] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [pricePerPage, setPricePerPage] = useState<number>(0);
    const [documentPageCount, setDocumentPageCount] = useState<number>(0);
    const [addFundsPermission, setAddFundsPermission] = useState<boolean>(false);

    const isBookSelected =
        (selectedState && selectedCounty && selectedBookType === BookType.INDEXES && selectedIndexType && dateRange && selectedIndexBook) ||
        (selectedState && selectedCounty && selectedBookType === BookType.RECORDED_DOCUMENTS && selectedRecordedBook);

    const pageCount = pageFrom && pageTo ? Math.max(0, parseInt(pageTo) - parseInt(pageFrom) + 1) : 0;
    const tokenCost = pageCount * pricePerPage;
    const isTokenCostExceeding = tokenCost > tokenBalance;

    useEffect(() => {
        const init = async (): Promise<void> => {
            setLoading(true);

            const user = await userService.getCurrentUser();
            setCurrentUser(user);

            setAddFundsPermission(userService.isPermitted(UserPermission.PURCHASE_TOKENS));

            const states = await stateService.getStates();
            const filteredStates = states.filter((state: State) => state.state === "South Carolina");
            setStateOptions(filteredStates.map((state: State) => ({key: state.id, text: state.state, value: state.id})));

            const counties = await countyService.getCounties();
            setCounties(counties);

            const costTypes = await subscriptionService.getCostTypes();
            if (Array.isArray(costTypes)) {
                const pageDownloadCost = costTypes.find((cost: any) => cost.actionType === ActionType.PAGE_DOWNLOAD);
                if (pageDownloadCost) {
                    setPricePerPage(pageDownloadCost.tokensRequired);
                }
            }

            if (user)
                await fetchTokenBalance(user.organizationID);

            setLoading(false);
        }
        init();
    }, []);

    useEffect(() => {
        if (selectedDocumentUrl){
            fetchDocumentPageCount(selectedDocumentUrl);
        }
    }, [selectedDocumentUrl]);

    const fetchTokenBalance = async (organizationID: string): Promise<void> => {
        const balanceResponse = await subscriptionService.getTokenBalance(organizationID);
        if (balanceResponse)
            setTokenBalance(balanceResponse.balance);
    }

    const fetchDocumentPageCount = async (documentUrl:string): Promise<void> => {
        if (documentUrl) {
            const pageCount = await documentService.getPageCount(documentUrl);
            setDocumentPageCount(pageCount);
        }
    }

    const fetchBookTypes = async (stateID: string, countyID: string): Promise<void> => {
        if (stateID && countyID) {
            setLoading(true);

            const bookTypesData = await marketplaceService.getDocumentMetadata(MetadataType.BOOK_TYPE, stateID, countyID);
            setBookTypes(bookTypesData || []);

            setLoading(false);
        } else
            setBookTypes([]);
    }

    const fetchIndexTypes = async (stateID: string, countyID: string, bookType: string): Promise<void> => {
        if (stateID && countyID && bookType) {
            setLoading(true);

            const indexTypesData = await marketplaceService.getDocumentMetadata(MetadataType.INDEX_TYPE, stateID, countyID, bookType);
            setIndexTypes(indexTypesData || []);

            setLoading(false);
        } else
            setIndexTypes([]);
    }

    const fetchDateRanges = async (stateID: string, countyID: string, indexType: string): Promise<void> => {
        if (stateID && countyID && indexType) {
            setLoading(true);

            const dateRangesData = await marketplaceService.getDocumentMetadata(MetadataType.DATE_RANGE, stateID, countyID, indexType);
            setDateRanges(dateRangesData || []);

            setLoading(false);
        } else
            setDateRanges([]);
    }

    const fetchIndexBooks = async (stateID: string, countyID: string, indexType: string, dateRangeValue: string): Promise<void> => {
        if (stateID && countyID && indexType && dateRangeValue) {
            setLoading(true);

            const indexBooksData = await marketplaceService.getDocumentMetadata(MetadataType.INDEX_BOOK, stateID, countyID, indexType, dateRangeValue);
            setIndexBooks(indexBooksData || []);

            setLoading(false);
        } else
            setIndexBooks([]);
    }

    const fetchRecordedBooks = async (stateID: string, countyID: string): Promise<void> => {
        if (stateID && countyID) {
            setLoading(true);

            const recordedBooksData = await marketplaceService.getDocumentMetadata(MetadataType.BOOK, stateID, countyID);
            setRecordedBooks(recordedBooksData || []);

            setLoading(false);
        } else
            setRecordedBooks([]);
    }

    const filterMetadata = async (payload: FilterMetadataRequestDto): Promise<void> => {
        setLoading(true);

        const response = await marketplaceService.filterMetaData(payload);
        if (response && response.length > 0) {
            const firstDocument = response[0];
            if (firstDocument) {
                setSelectedDocumentUrl(firstDocument.marketplaceUrl);
                setSelectedDocumentName(firstDocument.documentKey);
                setSelectedDocumentMetadataID(firstDocument.id);
            }
        }
        setLoading(false);
    }

    const handleStateChange = (event: SelectChangeEvent): void => {
        setSelectedState(event.target.value);

        setSelectedCounty('');
        setSelectedBookType('');
        setSelectedIndexType('');
        setDateRange('');
        setSelectedIndexBook('');
        setSelectedRecordedBook('');
        setPageFrom('');
        setPageTo('');
        setSelectedDocumentUrl('');
        setSelectedDocumentName('');
        setSelectedDocumentMetadataID('');

        setBookTypes([]);
        setIndexTypes([]);
        setDateRanges([]);
        setIndexBooks([]);
        setRecordedBooks([]);

        const stateCounties = counties.filter((county: County) => county.stateID === event.target.value && county.name === "Berkeley").sort((a: County, b: County) => a.name.localeCompare(b.name));
        setCountyOptions(stateCounties.map((county: County) => ({key: county.id, text: county.name, value: county.id})));
    }

    const handleCountyChange = async (event: SelectChangeEvent): Promise<void> => {
        setSelectedCounty(event.target.value);

        setSelectedBookType('');
        setSelectedIndexType('');
        setDateRange('');
        setSelectedIndexBook('');
        setSelectedRecordedBook('');
        setPageFrom('');
        setPageTo('');
        setSelectedDocumentUrl('');
        setSelectedDocumentName('');
        setSelectedDocumentMetadataID('');

        setBookTypes([]);
        setIndexTypes([]);
        setDateRanges([]);
        setIndexBooks([]);
        setRecordedBooks([]);

        if (selectedState && event.target.value)
            await fetchBookTypes(selectedState, event.target.value);
    }

    const handleBookTypeChange = async (event: SelectChangeEvent): Promise<void> => {
        setSelectedBookType(event.target.value);

        setSelectedIndexType('');
        setDateRange('');
        setSelectedIndexBook('');
        setSelectedRecordedBook('');
        setPageFrom('');
        setPageTo('');
        setSelectedDocumentUrl('');
        setSelectedDocumentName('');
        setSelectedDocumentMetadataID('');

        setIndexTypes([]);
        setDateRanges([]);
        setIndexBooks([]);
        setRecordedBooks([]);

        if (selectedState && selectedCounty && event.target.value)
            if (event.target.value === BookType.INDEXES)
                await fetchIndexTypes(selectedState, selectedCounty, event.target.value);
            else
                await fetchRecordedBooks(selectedState, selectedCounty);
    }

    const handleIndexTypeChange = async (event: SelectChangeEvent): Promise<void> => {
        setSelectedIndexType(event.target.value);

        setDateRange('');
        setSelectedIndexBook('');
        setPageFrom('');
        setPageTo('');
        setSelectedDocumentUrl('');
        setSelectedDocumentName('');
        setSelectedDocumentMetadataID('');

        setDateRanges([]);
        setIndexBooks([]);

        if (selectedState && selectedCounty && selectedBookType && event.target.value)
            await fetchDateRanges(selectedState, selectedCounty, event.target.value);
    }

    const handleDateRangeChange = async (event: SelectChangeEvent): Promise<void> => {
        setDateRange(event.target.value);

        setSelectedIndexBook('');
        setPageFrom('');
        setPageTo('');
        setSelectedDocumentUrl('');
        setSelectedDocumentName('');
        setSelectedDocumentMetadataID('');

        setIndexBooks([]);

        if (selectedState && selectedCounty && selectedIndexType && event.target.value)
            await fetchIndexBooks(selectedState, selectedCounty, selectedIndexType, event.target.value);
    }

    const handleIndexBookChange = async (event: SelectChangeEvent): Promise<void> => {
        setSelectedIndexBook(event.target.value);

        setPageFrom('');
        setPageTo('');

        if (selectedState && selectedCounty && selectedBookType === BookType.INDEXES && selectedIndexType && dateRange && event.target.value) {
            await filterMetadata({
                stateID: selectedState,
                countyID: selectedCounty,
                bookType: BookType.INDEXES,
                indexType: selectedIndexType,
                dateRange: dateRange,
                indexBook: event.target.value
            });
        }
    }

    const handleRecordedBookChange = async (event: SelectChangeEvent): Promise<void> => {
        setSelectedRecordedBook(event.target.value);

        setPageFrom('');
        setPageTo('');

        if (selectedState && selectedCounty && selectedBookType === BookType.RECORDED_DOCUMENTS && event.target.value) {
            await filterMetadata({
                stateID: selectedState,
                countyID: selectedCounty,
                bookType: BookType.RECORDED_DOCUMENTS,
                book: event.target.value
            });
        }
    }

    const handleTokenCallback = async (): Promise<void> => {
        setLoading(true);
        await fetchTokenBalance(currentUser?.organizationID ?? '');
        setLoading(false);
    }

    const handleValidate = (values: any): Record<string, string> => {
        const errors: Record<string, string> = {};

        if (!values.pageFrom || values.pageFrom.toString().trim() === '') {
            errors.pageFrom = 'From page is required';
        } else {
            const fromPageValue = parseFloat(values.pageFrom.toString());
            if (isNaN(fromPageValue)) {
                errors.pageFrom = 'From page must be a valid number';
            } else if (fromPageValue < 1) {
                errors.pageFrom = 'From page must be greater than 0';
            }
        }

        if (!values.pageTo || values.pageTo.toString().trim() === '') {
            errors.pageTo = 'To page is required';
        } else {
            const toPageValue = parseFloat(values.pageTo.toString());
            if (isNaN(toPageValue)) {
                errors.pageTo = 'To page must be a valid number';
            } else if (toPageValue < 1) {
                errors.pageTo = 'To page must be greater than 0';
            } else if (toPageValue > documentPageCount && documentPageCount > 0) {
                errors.pageTo = `To page cannot exceed ${documentPageCount} pages`;
            }
        }

        if (values.pageFrom && values.pageTo) {
            const fromPageValue = parseFloat(values.pageFrom.toString());
            const toPageValue = parseFloat(values.pageTo.toString());
            if (!isNaN(fromPageValue) && !isNaN(toPageValue) && fromPageValue > toPageValue) {
                errors.pageTo = 'To page must be greater than or equal to From page';
            }
        }

        return errors;
    }

    return (
        <>
            <Box className={'page-container'} onContextMenu={(e) => e.preventDefault()}>
                <Box className={'marketplace-page-header'}>
                    <Typography variant='h5' component='h1' className={'marketplace-page-title'}>
                        Search County Records
                    </Typography>
                    <Typography variant='body2' className={'marketplace-page-subtitle'}>
                        Unlock exclusive access to never before digitized documents.
                    </Typography>
                </Box>
                <Grid container spacing={3}>
                    <Grid size={2.7}>
                        <Paper elevation={0} className={'marketplace-search-paper'}>
                            <Box className={'marketplace-search-header'}>
                                <SearchIcon className={'marketplace-search-icon'}/>
                                <Typography variant='h6' component='h2' className={'marketplace-search-title'}>Document Search</Typography>
                            </Box>
                            <Typography variant='body2' color="text.secondary" className={'marketplace-search-description'}>
                                Search and browse through our extensive collection of digitized documents.
                            </Typography>
                            <Formik
                                initialValues={marketplaceService.getMarketplaceSearchDto()} validate={handleValidate} onSubmit={() => {}}>
                                {({values, errors, touched, handleBlur, setFieldValue}: FormikProps<MarketplaceSearchDto>): JSX.Element => (
                                    <Form>
                                        <Box className={'marketplace-form-container'}>
                                            <FormControl fullWidth>
                                                <InputLabel>Select State</InputLabel>
                                                <Select
                                                    value={values.selectedState}
                                                    label='Select State'
                                                    onChange={async (event: SelectChangeEvent): Promise<void> => {
                                                        await setFieldValue('selectedState', event.target.value);
                                                        await setFieldValue('selectedCounty', '');
                                                        await setFieldValue('selectedBookType', '');
                                                        await setFieldValue('selectedIndexType', '');
                                                        await setFieldValue('dateRange', '');
                                                        await setFieldValue('selectedIndexBook', '');
                                                        await setFieldValue('selectedRecordedBook', '');
                                                        await setFieldValue('pageFrom', '');
                                                        await setFieldValue('pageTo', '');

                                                        handleStateChange(event)}}>
                                                    {
                                                        stateOptions.map((state: DropdownOption): JSX.Element =>
                                                            <MenuItem key={state.key} value={state.value}>{state.text}</MenuItem>
                                                        )
                                                    }
                                                </Select>
                                            </FormControl>
                                            <FormControl fullWidth>
                                                <InputLabel>Select County</InputLabel>
                                                <Select
                                                    value={values.selectedCounty}
                                                    label='Select County'
                                                    onChange={async (event: SelectChangeEvent) => {
                                                        await setFieldValue('selectedCounty', event.target.value);
                                                        await setFieldValue('selectedBookType', '');
                                                        await setFieldValue('selectedIndexType', '');
                                                        await setFieldValue('dateRange', '');
                                                        await setFieldValue('selectedIndexBook', '');
                                                        await setFieldValue('selectedRecordedBook', '');
                                                        await setFieldValue('pageFrom', '');
                                                        await setFieldValue('pageTo', '');

                                                        await handleCountyChange(event);
                                                    }}
                                                    disabled={!values.selectedState}>
                                                    {
                                                        countyOptions.map((county: DropdownOption): JSX.Element =>
                                                            <MenuItem key={county.key} value={county.value}>{county.text}</MenuItem>
                                                        )
                                                    }
                                                </Select>
                                            </FormControl>
                                            <FormControl fullWidth>
                                                <InputLabel>Select Book Type</InputLabel>
                                                <Select
                                                    value={values.selectedBookType}
                                                    label='Select Book Type'
                                                    onChange={async (event: SelectChangeEvent): Promise<void> => {
                                                        await setFieldValue('selectedBookType', event.target.value);
                                                        await setFieldValue('selectedIndexType', '');
                                                        await setFieldValue('dateRange', '');
                                                        await setFieldValue('selectedIndexBook', '');
                                                        await setFieldValue('selectedRecordedBook', '');
                                                        await setFieldValue('pageFrom', '');
                                                        await setFieldValue('pageTo', '');

                                                        await handleBookTypeChange(event);
                                                    }}
                                                    disabled={!values.selectedState || !values.selectedCounty || loading}>
                                                    {
                                                        bookTypes.map((bookType: string): JSX.Element => <MenuItem key={bookType} value={bookType}>{bookType}</MenuItem>)
                                                    }
                                                </Select>
                                            </FormControl>
                                            {
                                                values.selectedBookType === BookType.RECORDED_DOCUMENTS &&
                                                <FormControl fullWidth>
                                                    <InputLabel>Select Book</InputLabel>
                                                    <Select
                                                        value={values.selectedRecordedBook}
                                                        label='Select Book'
                                                        disabled={loading}
                                                        onChange={async (event: SelectChangeEvent): Promise<void> => {
                                                            await setFieldValue('selectedRecordedBook', event.target.value);
                                                            await setFieldValue('pageFrom', '');
                                                            await setFieldValue('pageTo', '');

                                                            await handleRecordedBookChange(event)}}>
                                                        {
                                                            recordedBooks.map((book: string): JSX.Element => <MenuItem key={book} value={book}>{book}</MenuItem>)
                                                        }
                                                    </Select>
                                                </FormControl>
                                            }
                                            {
                                                values.selectedBookType === BookType.INDEXES &&
                                                <FormControl fullWidth>
                                                    <InputLabel>Select Index Type</InputLabel>
                                                    <Select
                                                        value={values.selectedIndexType}
                                                        label='Select Index Type'
                                                        disabled={loading}
                                                        onChange={async (event: SelectChangeEvent): Promise<void> => {
                                                            await setFieldValue('selectedIndexType', event.target.value);
                                                            await setFieldValue('dateRange', '');
                                                            await setFieldValue('selectedIndexBook', '');
                                                            await setFieldValue('pageFrom', '');
                                                            await setFieldValue('pageTo', '');

                                                            await handleIndexTypeChange(event);}}>
                                                        {
                                                            indexTypes.map((indexType: string): JSX.Element => <MenuItem key={indexType} value={indexType}>{indexType}</MenuItem>)
                                                        }
                                                    </Select>
                                                </FormControl>
                                            }
                                            {
                                                values.selectedBookType === BookType.INDEXES && values.selectedIndexType &&
                                                <FormControl fullWidth>
                                                    <InputLabel>Select Date Range</InputLabel>
                                                    <Select
                                                        value={values.dateRange}
                                                        label='Select Date Range'
                                                        disabled={loading}
                                                        onChange={async (event: SelectChangeEvent): Promise<void> => {
                                                            await setFieldValue('dateRange', event.target.value);
                                                            await setFieldValue('selectedIndexBook', '');
                                                            await setFieldValue('pageFrom', '');
                                                            await setFieldValue('pageTo', '');

                                                            await handleDateRangeChange(event)}}>
                                                        {
                                                            dateRanges.map((range: string): JSX.Element => <MenuItem key={range} value={range}>{range}</MenuItem>)
                                                        }
                                                    </Select>
                                                </FormControl>
                                            }
                                            {
                                                values.selectedBookType === BookType.INDEXES && values.selectedIndexType && values.dateRange &&
                                                <FormControl fullWidth>
                                                    <InputLabel>Select Index Book</InputLabel>
                                                    <Select
                                                        value={values.selectedIndexBook}
                                                        label='Select Index Book'
                                                        disabled={loading}
                                                        onChange={async (event: SelectChangeEvent): Promise<void> => {
                                                            await setFieldValue('selectedIndexBook', event.target.value);
                                                            await setFieldValue('pageFrom', '');
                                                            await setFieldValue('pageTo', '');

                                                            await handleIndexBookChange(event);
                                                        }}>
                                                        {
                                                            indexBooks.map((book: string): JSX.Element => <MenuItem key={book} value={book}>{book}</MenuItem>)
                                                        }
                                                    </Select>
                                                </FormControl>
                                            }

                                            <Divider className={'marketplace-purchase-divider'}/>
                                            <Box className={'marketplace-purchase-section'}>
                                                <Typography variant='h6' component='h3' className={'marketplace-purchase-title'}>
                                                    Purchase Pages
                                                </Typography>
                                                <Typography variant='body2' color='text.secondary' className={'marketplace-purchase-description'}>
                                                    Select the page range you want to purchase.
                                                </Typography>
                                                <Grid container spacing={2} className={'marketplace-page-inputs-container'}>
                                                    <Grid size={6} className={'marketplace-page-input-wrapper'}>
                                                        <TextField
                                                            fullWidth
                                                            label='From Page'
                                                            name='pageFrom'
                                                            type='number'
                                                            value={values.pageFrom}
                                                            onChange={async (e:  React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): Promise<void> => {
                                                                await setFieldValue('pageFrom', e.target.value);
                                                                setPageFrom(e.target.value);}}
                                                            onBlur={handleBlur}
                                                            error={touched.pageFrom && Boolean(errors.pageFrom)}
                                                            helperText={touched.pageFrom && errors.pageFrom}
                                                            inputProps={{min: 1}}/>
                                                    </Grid>
                                                    <Grid size={6} className={'marketplace-page-input-wrapper'}>
                                                        <TextField
                                                            fullWidth
                                                            label='To Page'
                                                            name='pageTo'
                                                            type='number'
                                                            value={values.pageTo}
                                                            onChange={async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): Promise<void> => {
                                                                await setFieldValue('pageTo', e.target.value);
                                                                setPageTo(e.target.value);}}
                                                            onBlur={handleBlur}
                                                            error={touched.pageTo && Boolean(errors.pageTo)}
                                                            helperText={touched.pageTo && errors.pageTo}
                                                            inputProps={{min: 1}}/>
                                                    </Grid>
                                                </Grid>

                                                <Box className={'marketplace-purchase-summary'}>
                                                    <Typography variant='body2' className={'marketplace-purchase-summary-text'}>
                                                        <span>Available Tokens:</span>
                                                        <span>{tokenBalance.toLocaleString()}</span>
                                                    </Typography>
                                                </Box>

                                                <Box className={`marketplace-purchase-summary ${isTokenCostExceeding ? 'token-cost-exceeding' : ''}`}>
                                                    <Typography variant='body2' className={`marketplace-purchase-summary-text ${isTokenCostExceeding ? 'token-cost-exceeding' : ''}`}>
                                                        <span>Pages: {pageCount}</span>
                                                        <span>Token Cost: {tokenCost}</span>
                                                    </Typography>
                                                </Box>

                                                <Button
                                                    variant='contained'
                                                    fullWidth
                                                    className={'zitles-button submit marketplace-purchase-button'}
                                                    disabled={!values.pageFrom || !values.pageTo || pageCount <= 0 || isTokenCostExceeding || Boolean(errors.pageFrom) || Boolean(errors.pageTo) || !selectedDocumentUrl}
                                                    onClick={(): void => setOpenPurchaseDialog(true)}>
                                                    {
                                                        !values.pageFrom || !values.pageTo || pageCount <= 0
                                                            ? 'Purchase'
                                                            : `Purchase ${values.pageFrom}-${values.pageTo}`
                                                    }
                                                </Button>

                                                {
                                                    addFundsPermission &&
                                                        <Button
                                                            variant='contained'
                                                            fullWidth
                                                            onClick={() => setOpenAddFundsDialog(true)}
                                                            className={'zitles-button submit marketplace-add-funds-button'}>
                                                            Add Funds
                                                        </Button>
                                                }
                                            </Box>
                                        </Box>
                                    </Form>
                                )}
                            </Formik>
                        </Paper>
                    </Grid>
                    <Grid size={9}>
                        <Paper elevation={0} className={'marketplace-document-paper'}>
                            {
                                isBookSelected && selectedDocumentUrl
                                    ? <Box className={'marketplace-document-content'}>
                                        <Box className={'marketplace-document-viewer-area'}>
                                            <MarketplaceDocumentViewer
                                                documentUrl={selectedDocumentUrl}
                                                documentName={selectedDocumentName || (selectedBookType === BookType.INDEXES ? selectedIndexBook : selectedRecordedBook)}/>
                                        </Box>
                                    </Box>
                                    : <Box className={'marketplace-empty-state'}>
                                        <Box className={'marketplace-empty-state-content'}>
                                            <SearchIcon className={'marketplace-empty-state-icon'}/>
                                            <Typography variant='h6' component='h4' className={'marketplace-empty-state-title'}>
                                                Select Document to Preview
                                            </Typography>
                                            <Typography variant='body2' color='text.secondary' className={'marketplace-empty-state-description'}>
                                                Choose your state, county, and book type from the sidebar to begin searching
                                                for documents.
                                            </Typography>
                                        </Box>
                                    </Box>
                            }
                        </Paper>
                    </Grid>
                </Grid>
                {
                    openPurchaseDialog &&
                        <PurchaseDialog
                            open={openPurchaseDialog}
                            setOpen={setOpenPurchaseDialog}
                            pageFrom={pageFrom}
                            pageTo={pageTo}
                            documentMetadataID={selectedDocumentMetadataID}
                            tokenCallback={handleTokenCallback}/>
                }
                {
                    openAddFundsDialog &&
                        <AddFundDialog open={openAddFundsDialog} setOpen={setOpenAddFundsDialog}/>
                }
                {
                    loading &&
                        <LoadingComponent/>
                }
            </Box>
        </>
    );
}

export default observer(Marketplace);
